// Modules
import React from "react";
import moment from "moment";
import safeJsonStringify  from 'safe-json-stringify';

// @ts-ignore
import { LocalizationContext } from "react-localize";

// Styles
import "./ApplyYourPoints.sass";

// Services
import ReservationService from "Services/reservation.service";

// Interfaces
import {
  ContractUsage,
  AYPResult,
  ReservationDetails,
  WaitlistAYPResult,
  UsageRequiredByDay,
  SuggestedAutoApply,
  AutoAdjustUsage,
  WaitlistRooms
} from "Interfaces/AYPResultInterface";
import {RequestError} from "Interfaces/RequestError";

// Components
import BookingInfo from "./BookingInfo";
import { MvSortTable } from "Generic/MvSortTable";
import BorrowToUsePoints from "./BorrowToUsePoints";
import AppliedPtsInput from "./AppliedPtsInput";
import {PointsSummary} from "./PointsSummary";
import MvLoader from "Generic/MvLoader/MvLoader";
import {AdditionalGuestProfile} from '../Summary/AdditionalGuestProfile';

// Utils
import {sortContracts, SortType, SortDirection} from "Utils/SortContracts";

// Constants
import {RequestStatus, ScreenType} from "Constants";
import {MvConfirmationModal} from "../../../Generic/MvConfirmationModal/MvConfirmationModal";
import MvContractToolTip from "Generic/ToolTip/MvContractToolTip";
import ContractToolTip from "Interfaces/ContractToolTipInterface";

// @ts-ignore
import sessionStorage from "store/storages/sessionStorage";
import ToolTip from "Generic/ToolTip";
import { Tip } from "Interfaces/TipInterface";
import { Position } from "Generic/ToolTip/ToolTip";
import { OwnerContract, OwnerShipDetails } from "Interfaces/OwnerInterface";
import LocalizationService from "Services/localization.service";
import { ReactAppMode } from "Interfaces/ReactAppModes";
import {EXEC_MODE} from "../../../cfg";
import BookingInfoExists from "./BookingInfoExists";
import { ReservationType } from "Interfaces/GetReservations";
import { logAnalyticData, trackAnalyticData } from "Utils/analytics";


type Props = {
  token: string;
  lang: string;
  resId: number;
  // waitlistId?: number;
  setScreenType(screenType: ScreenType): void;
  apiTimeout: string;
}

enum PointsTableType {
  ELIGIBLE,
  INELIGIBLE,
  WAITLIST
}

type State = {
  usageRequiredByDay: UsageRequiredByDay[];
  showCancelModal: boolean;
  contractsRequestStatus: RequestStatus,
  reservationDetails: ReservationDetails,
  [PointsTableType.ELIGIBLE]: {
    contracts: ContractUsage[];
    [SortType.CONTRACT_SORT]: SortDirection;
    [SortType.DATE_SORT]: SortDirection;
    [SortType.USAGE_TYPE_SORT]: SortDirection;
    [SortType.USAGE_ORDER]: SortDirection;
  },
  [PointsTableType.INELIGIBLE]: {
    contracts: ContractUsage[];
    [SortType.CONTRACT_SORT]: SortDirection;
    [SortType.DATE_SORT]: SortDirection;
    [SortType.USAGE_TYPE_SORT]: SortDirection;
    [SortType.USAGE_ORDER]: SortDirection;
  },
  [PointsTableType.WAITLIST]: {
    contracts: ContractUsage[];
    [SortType.CONTRACT_SORT]: SortDirection;
    [SortType.DATE_SORT]: SortDirection;
    [SortType.USAGE_TYPE_SORT]: SortDirection;
    [SortType.USAGE_ORDER]: SortDirection;
  },
  contractList: [];
  contractToolTips: ContractToolTip[]
  contractToolTipsRequestStatus: RequestStatus;
  hasNoEligiblePoints: boolean;
  suggestedAutoApply: SuggestedAutoApply[];
  PromotionIDs: Number[];
  showAutoAdjustError: boolean;
  autoAdjustUsage: AutoAdjustUsage[]
  isMobile: boolean;
};

const InitState: State = {
  usageRequiredByDay: [],
  showCancelModal: false,
  contractsRequestStatus: RequestStatus.LOADING,
  reservationDetails: {
    resortName: "",
    resortLocation: "",
    resortImage: "",
    roomType: { description: "" },
    checkInDate: "",
    checkOutDate: "",
    numOfNights: 0,
    numOfGuests: 0,
    accessibility: [],
    hearingaccessible: false,
    pointsRequired: 0
  },
  [PointsTableType.ELIGIBLE]: {
    contracts: [],
    [SortType.CONTRACT_SORT]: SortDirection.ASC,
    [SortType.DATE_SORT]: SortDirection.ASC,
    [SortType.USAGE_TYPE_SORT]: SortDirection.ASC,
    [SortType.USAGE_ORDER]: SortDirection.ASC
  },
  [PointsTableType.INELIGIBLE]: {
    contracts: [],
    [SortType.CONTRACT_SORT]: SortDirection.ASC,
    [SortType.DATE_SORT]: SortDirection.ASC,
    [SortType.USAGE_TYPE_SORT]: SortDirection.ASC,
    [SortType.USAGE_ORDER]: SortDirection.ASC
  },
  [PointsTableType.WAITLIST]: {
    contracts: [],
    [SortType.CONTRACT_SORT]: SortDirection.ASC,
    [SortType.DATE_SORT]: SortDirection.ASC,
    [SortType.USAGE_TYPE_SORT]: SortDirection.ASC,
    [SortType.USAGE_ORDER]: SortDirection.ASC
  },
  contractList: [],
  contractToolTips: [],
  contractToolTipsRequestStatus: RequestStatus.LOADING,
  hasNoEligiblePoints: false,
  isMobile: false,
  suggestedAutoApply: [],
  PromotionIDs: [],
  showAutoAdjustError: false,
  autoAdjustUsage: []
};

class ApplyYourPoints extends React.Component<Props, State> {
  localizationService: LocalizationService;
  reservationService: ReservationService;
  _isMounted: boolean;

  constructor(props: Props) {
    super(props);
    this.state = Object.assign({}, InitState);
    this.localizationService = new LocalizationService();
    this.reservationService = new ReservationService();
    this.getPointsNeeded = this.getPointsNeeded.bind(this);
    this.goToSelectAvailability = this.goToSelectAvailability.bind(this);
    this._isMounted = false;
    this.resizeHandler = this.resizeHandler.bind(this);
    this.editSegment = this.editSegment.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchContractUsages();

    // Scroll to top of document
    window.scrollTo(0, 0);
    
    window.onresize = this.resizeHandler;
    this.setState({
      isMobile: window.innerWidth < 1060,
    });
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  resizeHandler() {
    this.setState({
      isMobile: window.innerWidth < 1060,
    });
  }

  editSegment() {
    this.props.setScreenType(ScreenType.SEARCH_RESORTS);
  }

  /**
   * fetches contract usages for this reservation
   */
  fetchContractUsages(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.reservationService.getUserContracts(this.props.resId)
        .then((aypResult: AYPResult) => {
          const {
            reservationDetails,
            ineligibleUsages,
            usageRequiredByDay,
            suggestedAutoApply,
            PromotionIDs
          } = aypResult;

          // check for applied points in storage and use if exists
          const sessionContracts: ContractUsage[] = JSON.parse(sessionStorage.read(ReservationService.APPLIED_POINTS));

          let { eligibleUsages } = aypResult;

          if (sessionContracts) {
            eligibleUsages = sessionContracts;
          }

          // Set borrowChecked for each eligible contract usage.
          // If a contract is allowed to borrow =>
          //    If points are recommended to be applied =>
          //        borrow is checked.
          eligibleUsages.forEach((contract: ContractUsage) => {
            contract.borrowChecked = false;
            if (contract.canBorrow === true) {
              if (contract.appliedPoints > 0) {
                contract.borrowChecked = true;
              } else {
                // Disable future contracts that aren't borrowing
                contract.disabled = true;
              }
            }
          });

          // get a unique list of contract Ids
          const contractList: any = new Set([...eligibleUsages, ...ineligibleUsages].map(usages => usages.contractId));

          const hasNoEligiblePoints = eligibleUsages.length === 0; //may need to filter out disabled contracts??

          this.setState({
            usageRequiredByDay,
            reservationDetails,
            contractsRequestStatus: RequestStatus.LOADED,
            [PointsTableType.ELIGIBLE]: {
              ...this.state[PointsTableType.ELIGIBLE],
              contracts: sortContracts(eligibleUsages),
            },
            [PointsTableType.INELIGIBLE]: {
              ...this.state[PointsTableType.INELIGIBLE],
              contracts: sortContracts(ineligibleUsages),
            },
            contractList,
            hasNoEligiblePoints: hasNoEligiblePoints,
            suggestedAutoApply: suggestedAutoApply || [],
            PromotionIDs
          });
          resolve(contractList);
        })
        .then((contractList: any) => {
          this.fetchContactTooltips(contractList)
            .then(() => {
              resolve();
            })
        })
        .catch((error: RequestError) => {
          this.setState({ contractsRequestStatus: RequestStatus.ERRORED });

          // Invalid or missing reserved inventory ID, go back to search
          if (error.errorCode === "NO_HELD_INVENTORY_FOR_OWNER") {
            //this.goToSelectAvailability();
          }
          reject(error);
        });
    });
  }
  /**
   * Gets and stores the contract tooltip data for all contracts
   */
  fetchContactTooltips(contractList: any) {
    return new Promise((resolve, reject) => {

      // get the contracts' data
      this.reservationService.getOwnerContracts(this.state.contractList)
        .then((ownerContracts: OwnerContract[]) => {

          // build the ContractToolTip[]
          let contractToolTips: ContractToolTip[] = [];
          ownerContracts.map(contract => {
            // convert OwnerContract to ContractToolTip
            let tempContractToolTip = this.convertOCtoCTT(contract);
            // get the tooltip jsx
            tempContractToolTip.toolTip = this.getContractToolTipJSX(tempContractToolTip);
            contractToolTips.push(tempContractToolTip);
          });

          if (this._isMounted) {
            this.setState({
              contractToolTips,
              contractToolTipsRequestStatus: RequestStatus.LOADED,
            });
          }
          resolve();
        })
        .catch((error: RequestError) => {
          if (this._isMounted) {
            this.setState({ contractToolTipsRequestStatus: RequestStatus.ERRORED });
          }
          reject(error);
        });
    });
  }

  convertOCtoCTT(oc: OwnerContract): ContractToolTip {
    // the AYP table is still returning mock data? that will be returning real
    //  data this sprint by Rama - done by tomorrow perhaps
    // getting CORS error
    return {
        id: oc.id, 
        contractId: oc.contractNumber, 
        contractType: oc.contractType,
        // hard code to points for now
        // the service is not providing this field 
        // it is also not returning any weeks data
        typeName: "points", 
        startDate: oc.contractStartDate,
        endDate: oc.contractEndDate,
        // this field is missing from the service
        // it is being looked into
        // so for now, just show the start date's year
        usageYear: oc.contractStartDate,
        points: oc.contractPoints,
        ownershipDetails: oc.ownershipDetails ? oc.ownershipDetails : {} as OwnerShipDetails //CE2RT1C-899 - 6. add ownersheip details to contract tool tip
      };
  }

  /**
   * Converts the contract info into a ContracToolTip
   */
  getContractToolTipJSX(contractToolTip: ContractToolTip): any {
    const contractId = contractToolTip.contractId;
    const key = contractId + contractToolTip.startDate + contractToolTip.endDate + (contractToolTip.points ? contractToolTip.points.toString() : '');
    const contractIdStyle = <p key={key} className="contract-id" aria-label={contractId}>{contractId}</p>

    return <MvContractToolTip contract={contractToolTip} position={Position.RIGHT}>
            {contractIdStyle}
          </MvContractToolTip>;
  }

  cancelConfirmed = () => {
      const { localize } = this.context;
      this.reservationService.deleteHeldResorts(this.props.resId)
          .then(() => {
            //this.setState({showCancelModal: !this.state.showCancelModal});
            //sessionStorage.remove(ReservationService.OPS_FORM_SESSION_KEY);
            sessionStorage.remove(ReservationService.APPLIED_POINTS);
            this.goToSelectAvailability();
          })
          .catch((error: any) => {
            // const errorMessage = error.message ? `${error.message}` : "Unspecified error";
            const errorMessage = error.message ? `${error.message}` : localize('static.content.pwas-search-fatal-error-message');
            //NOTE: seems like it makes sense to navigate back to search availability screen
            //even if we failed to release a hold on reservation. The res hold might be already expired as an example
            //sessionStorage.remove(ReservationService.OPS_FORM_SESSION_KEY);
            this.goToSelectAvailability();
          });
  }

  /**
   * fired when 'Reset' btn is clicked.
   *
   * All user applied points entries are cleared.
   * Recommended TSW allocation of points is restored.
   * All 'Ways to Apply' check boxes are deselected,
   * except those that were originally recommended.
   */

  resetSelections = ()  => {
    // TODO: complete this function rt-731
    this.setState({ showAutoAdjustError: false });
    this.fetchContractUsages();

    /**
     * PWAS Analytics to Track User Experience
     * Apply your Points
     * 
     */
    logAnalyticData({ event: 'Search Resorts', siteSection: 'Points', pageName:'Apply Your Points', website:'' }, {
      ownerActivity: 'Points Reservation',
      step:'Step 4 : Apply Points-Reset',
      seaarch: "",
      errorMessagfe:"",
      notificationType: "",
      customerID:"",
      searchCheckInDate: "",    
    })   
    trackAnalyticData('points: Points Booking - Step 4 – Apply Points – Reset');
  }

  /**
   * used to navigate to search availabilty
   */
  goToSelectAvailability() {
    if ([ReactAppMode.VOA, ReactAppMode.LOCAL].includes(EXEC_MODE)) {
      this.props.setScreenType(ScreenType.SEARCH_RESORTS);
    }
    else {
      // VCAP (production)
      window.location.href = '/start_booking.do';
    }
  }

  /**
   * fired when the 'Continue' btn is clicked
   */
  continue = (disabled: boolean) => {
    const { localize } = this.context;

    if (disabled) return false;

    // set applied points to session storage
    const contracts = this.state[PointsTableType.ELIGIBLE].contracts;
    let result = this.state.PromotionIDs.map(i=>Number(i));
    const autoAdjustPayload = {
      promotionIDs: result,
      usageYears: contracts.map(x => ({ usageYearId: x.usageId, applied: x.appliedPoints,usageType:x.usageType }))
    };
    this.reservationService.autoAdjustUsage(autoAdjustPayload, this.props.resId)
    .then((response: any) => {
      if (response.autoAdjustUsageValidation) {
        sessionStorage.write(ReservationService.APPLIED_POINTS, safeJsonStringify(contracts));

        // set contract tool tips to session storage
        const contractToolTips = this.state.contractToolTips;
        sessionStorage.write(ReservationService.CONTRACT_TOOL_TIPS, safeJsonStringify(contractToolTips));
    
        // set usagesRequiredByDay in session for the book button call
        const usageRequiredByDay = this.state.usageRequiredByDay;
        sessionStorage.write(ReservationService.USAGE_REQUIRED_BY_DAY, safeJsonStringify(usageRequiredByDay));
    
        if ([ReactAppMode.VOA, ReactAppMode.LOCAL].includes(EXEC_MODE)) {
          this.props.setScreenType(ScreenType.SUMMARY_REVIEW);
        }
        else {
          // VCAP (production)
          const selectedAvailabilityState = sessionStorage.read(ReservationService.SELECT_AVAILABILITY_STATE);
          if (selectedAvailabilityState !== undefined) {
            window.history.pushState(JSON.parse(selectedAvailabilityState), '', 'start_booking.do');
          }
    
          window.location.href = `/summaryReviewPage.jsp?resId=${this.props.resId}`;
        }
      } else {
        this.setState({ showAutoAdjustError: true, autoAdjustUsage: response.autoAdjustUsage })
      }
      /*
      * PWAS Analytics when clicked on continue button
      * 
      */

      logAnalyticData({ event: 'Search Resorts', siteSection: 'Points Reservation', pageName:'Book My Holiday', website:'' }, {
        ownerActivity: 'Points Reservation',
        step:'Step 4:Apply Points',
        resortID:"",
        notificationType:"",
        errorMessagfe:"",
        typeOfSearch: "",
        filtersApplied:"",
        searchCheckInDate: "",    
      })
      
      trackAnalyticData('points:step4')

    })
    .catch((error: any) => {
      // const errorMessage = error.message ? `${error.message}` : "Unspecified error";
      const errorMessage = error.message ? `${error.message}` : localize('static.content.pwas-search-fatal-error-message');
      console.error(`Error: ${JSON.stringify(error)}`);
    });
  };

  /**
   * fired when a 'Borrow to use Points' checkbox is clicked
   */
  borrowCheckboxToggled = (checked: boolean, usageId: string) => {
    const state = {...this.state};
    const eligibleTable = {...state[PointsTableType.ELIGIBLE]};
    const contracts = [...eligibleTable.contracts];

    const contract = contracts.find((contractUsage: ContractUsage) => {
      return contractUsage.usageId === usageId;
    });

    // sanity check
    if (!contract) return null;

    if (checked) {
      contract.disabled = false;
      contract.borrowChecked = true;
    } else {
        contract.appliedPoints = 0;
        contract.borrowChecked = false;
        contract.disabled = true;
    }
    contracts.push(contract);
    this.setState({state} as any);
  }

  /**
   * fired when the 'Cancel' button is clicked
   */
  toggleCancelModal = () => {
    this.setState({showCancelModal: !this.state.showCancelModal});
  }

  /**
   * fired when a column sort icon is clicked in either the 'Apply Your Points'
   * table, OR the ineligible points table
   */
  onSortClicked(tableType: PointsTableType, sortField: SortType) {
    const table = {...this.state[tableType]};
    const contracts = [...table.contracts];
    const sortDirection = table[sortField] === SortDirection.ASC ?
        SortDirection.DESC : SortDirection.ASC;

    const sortedContracts = sortContracts(
        contracts,
        sortField,
        sortDirection
    );

    table[sortField] = sortDirection;
    table.contracts = [...sortedContracts];

    // reset other sort directions to ASC (all sort icons will return to ASC)
    if (sortField !== SortType.CONTRACT_SORT) {
      table[SortType.CONTRACT_SORT] = SortDirection.ASC;
    }
    if (sortField !== SortType.DATE_SORT) {
      table[SortType.DATE_SORT] = SortDirection.ASC;
    }

    this.setState({
      [tableType]: table,
    });
  }

  /**
   * return the array of headers for the ineligible ('Apply Your Points') table
   */
  ineligiblePointsTableHeaders() {
    const { localize } = this.context;
    return [
      {
        text: localize("static.content.ayp-contract"),
        sortable: true,
        curSortDirection: this.state[PointsTableType.INELIGIBLE][SortType.CONTRACT_SORT],
        onSort: () => {
          this.onSortClicked(PointsTableType.INELIGIBLE, SortType.CONTRACT_SORT);
        }
      },
      {
        text: localize("static.content.ayp-usage-type"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-usage-year"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-start-date"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-end-date"),
        sortable: true,
        curSortDirection: this.state[PointsTableType.INELIGIBLE][SortType.DATE_SORT],
        onSort: () => {
          this.onSortClicked(PointsTableType.INELIGIBLE, SortType.DATE_SORT);
        }
      },
      {
        text: localize("static.content.ayp-available"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-applied"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-reason"),
        sortable: false,
      },
    ]
  }

  /**
   * return the array of headers for the eligible ('Apply Your Points') table
   */
  eligiblePointsTableHeaders() {
    const { localize } = this.context;
    return [
      {
        text: localize("static.content.ayp-contract"),
        sortable: true,
        curSortDirection: this.state[PointsTableType.ELIGIBLE][SortType.CONTRACT_SORT],
        onSort: () => {
          this.onSortClicked(PointsTableType.ELIGIBLE, SortType.CONTRACT_SORT);
        }
      },
      {
        text: localize("static.content.ayp-usage-type"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-usage-year"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-start-date"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-end-date"),
        sortable: true,
        curSortDirection: this.state[PointsTableType.ELIGIBLE][SortType.DATE_SORT],
        onSort: () => {
          this.onSortClicked(PointsTableType.ELIGIBLE, SortType.DATE_SORT);
        }
      },
      {
        text: localize("static.content.ayp-available"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-applied"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-ways-to-apply-pts"),
        sortable: false,
      },
    ]
  }

  /**
   * fired when the applied points input is edited
   */
  usagePtsChange(usageId: string, value: number) {
    const contracts = [...this.state[PointsTableType.ELIGIBLE].contracts];
    const updatedContract = contracts.find((contract: ContractUsage) => {
      return contract.usageId === usageId;
    });

    // sanity check
    if (!updatedContract) {
      return false;
    }

    updatedContract.appliedPoints = value;

    this.setState({
      [PointsTableType.ELIGIBLE]: {
        ...this.state[PointsTableType.ELIGIBLE],
        contracts,
      },
    });
  }

  /**
   * Gets the ContractToolTip for the specific contractId
   * If it can't be found, return the generic ToolTip
   */
  getContractToolTip(contractId: string, useageId: string): any {
    const contractToolTips = [...this.state.contractToolTips];
    const contractToolTip = contractToolTips.find((ctt: ContractToolTip) => {
      return ctt.contractId === contractId;
    });

    return contractToolTip ? contractToolTip.toolTip : this.getContractToolTipGeneric(contractId, useageId);
  }

  /**
   * Gets the ToolTip for the specific contractId
   */
  getContractToolTipGeneric(contractId: string, useageId: string): any {
    const { localize } = this.context;
    const tip: [Tip]= [{ label: `${localize("static.content.ayp-contract")}`, desc: `${contractId}` }];
    const contractIdStyle = <p key={useageId} className="contract-id" aria-label={contractId}>{contractId}</p>;

    return <ToolTip useRefToPosition={false} id={`contract-generic-${contractId}`} tabIndex={-1} tip={tip} position={Position.RIGHT}>
            {contractIdStyle}
          </ToolTip>;
  }

  getStartDateJSX(startDate: string, endDate: string, className?: string) {
    const { localize } = this.context;
    const validDatesText = `${localize("static.content.ayp-valid-dates")}`;
    const start = moment(startDate).format("DD MMM YYYY");
    return <>
      <p className={className ? className : ''} aria-label={`${validDatesText} ${start}`}>
        <span className="header-text bold">
          {validDatesText}{":"}
        </span>
        {start} 
        <span className="header-text">
            {endDate ?  " - ": ""}
          </span>
      </p>
    </>
  }

  getUsageTypeJSX(usageType: string, className?: string): JSX.Element {
    const { localize } = this.context;
    const friendlyUsageTypeName = this.localizationService.getUsageTypeNameFromJahia(usageType).value;
    const usageTypeHeaderText = `${localize("static.content.ayp-usage-type")}`;


    return <>
      <p className={className ? className : ''} aria-label={`${usageTypeHeaderText} ${usageType}`}>
        <span className="header-text bold">
          {usageTypeHeaderText}{":"}
        </span>
        {friendlyUsageTypeName}
      </p>
    </>
  }

  /**
   * return the jsx for each cell in the eligible ('Apply Your Points') table
   */
  eligiblePointsTableRows() {

    const { localize } = this.context;
    
    if (this.state.hasNoEligiblePoints) {
      return new Array({
        rowKey: "-1",
        rowCells: {
          errorMessage: ( 
            <span className="ineligible-reason-container no-points-error">
              <i className="fa fa-exclamation-triangle"></i>
              <p aria-label={localize("static.content.ayp-no-points-error")}>{localize("static.content.ayp-no-points-error")}</p>
            </span>
          )
        }
      });
    }
    else {
      return this.state[PointsTableType.ELIGIBLE].contracts.map(contract => {
        //const startDate = moment(contract.startDate).format("DD MMM YYYY");
        const endDate = moment(contract.endDate).format("DD MMM YYYY");
        const available = (parseInt(contract.available) - contract.appliedPoints).toLocaleString();
        
        return {
          rowKey: contract.usageId,
          rowCells: {
            contractId: this.getContractToolTip(contract.contractNumber, contract.usageId),
            usageType: this.getUsageTypeJSX(contract.usageType),
            usageYear: <p aria-label={contract.usageYear}>{contract.usageYear}</p>,
            startDate: this.getStartDateJSX(contract.startDate, contract.endDate),
            endDate: <p aria-label={endDate}> {endDate}</p>,
            available: (
              <span className={contract.disabled === true ? 'disabled' : ''}>
                <p aria-label={available}>{available}</p>
              </span>
            ),
            applied: (
              <AppliedPtsInput
                maxValue={parseInt(contract.available)}
                value={contract.appliedPoints}
                onChange={(val) => this.usagePtsChange(contract.usageId, val)}
                id={`appliedPtsInput-${contract.usageId}`}
                disabled={contract.disabled === undefined ? false : contract.disabled}
              />
            ),
            waysToApply: (contract.canBorrow ?
                <BorrowToUsePoints
                  usageId={contract.usageId}
                  checked={contract.borrowChecked === true}
                  onCheck={this.borrowCheckboxToggled}
                />
                : null
              ),
          }
        };
      });
    }
  }

  /**
   * return the jsx for each cell in the ineligible ('Apply Your Points') table
   */
  ineligiblePointsTableRows() {
    return this.state[PointsTableType.INELIGIBLE].contracts.map(contract => {
      const startDate = moment(contract.startDate).format("DD MMM YYYY");
      const endDate = moment(contract.endDate).format("DD MMM YYYY");
      return {
        rowKey: contract.usageId,
        rowCells: {
          contractId: this.getContractToolTip(contract.contractNumber, contract.usageId),
          usageType: this.getUsageTypeJSX(contract.usageType, "disabled"),
          usageYear: <p className="disabled" aria-label={contract.usageYear}>{contract.usageYear}</p>,
          startDate: this.getStartDateJSX(contract.startDate, contract.endDate, "disabled"),
          endDate: <p className="disabled" aria-label={endDate}>{endDate}</p>,
          available: <p className="disabled" aria-label={contract.available.toLocaleString()}>{contract.available.toLocaleString()}</p>,
          applied: (<input
              value={0}
              disabled
              className="applied-pts-input"
          />),
          reason: (
            <div className="ineligible-reason-container">
              <i className="fa fa-exclamation-triangle"></i>
              <p className="contract-reason">
                {(contract.reason || []).map((reason: any, i: any) => (
                  <p>{reason}</p>
                 ))}
              </p>
            </div>
          ),
        },
      };
    });
  }

  /**
   * return the total points applied
   */
  getPointsApplied(): number {
    const contracts = this.state[PointsTableType.ELIGIBLE].contracts;

    const total =  contracts.reduce((total:number, contract: ContractUsage) => {
      // times by 1 because it was concating numbers for some reason
      return (total + contract.appliedPoints) * 1;
    }, 0);

    return total;
  }

  /**
   * return the points still needed (pointsRequired - pointsApplied)
   */
  getPointsNeeded(): number {
    const pointsRequired = this.state.reservationDetails.pointsRequired;
    const pointsApplied = this.getPointsApplied();

    return pointsRequired - pointsApplied;
  }

  //we saved Select Availability state into the browser's history before forwarding to AYP page
  //so, here we just simulate back button click on Browser
  goBackToSelectAvailabilityElementOnSearchScreen = () => {
    sessionStorage.remove(ReservationService.APPLIED_POINTS);

    if ([ReactAppMode.VOA, ReactAppMode.LOCAL].includes(EXEC_MODE)) {
      this.props.setScreenType(ScreenType.SEARCH_RESORTS);
    }
    else {
      // VCAP (production)
      window.history.back();
    }

    /**
     * PWAS Analytics to Track User Experience
     * Apply your  - Edit Search
     * 
     */
    logAnalyticData({ event: 'Search Resorts', siteSection: 'Points', pageName:'Book My Holiday', website:'' }, {
      ownerActivity: 'Points Reservation',
      step:'Step 4 : Apply Points-Edit Search',
      seaarch: "",
      errorMessagfe:"",
      notificationType: "",
      customerID:"",
      searchCheckInDate: "",    
    })   
    trackAnalyticData('Points Booking - Step 4 – Apply Points – Edit Search');

  };

  render() {
    const { localize } = this.context;
    const pointsRequired = this.state.reservationDetails.pointsRequired;
    return (
        <>
          <MvConfirmationModal
              header = ""
              description = {localize("static.content.ayp-cancel-transaction")}
              message = {localize("static.content.ayp-cancel-transaction-warning")}
              abortLabel = {localize("static.content.ayp-do-not-cancel-transaction")}
              confirmLabel = {localize("static.content.ayp-do-cancel-transaction")}
              openModal={this.state.showCancelModal}
              toggleModal={this.toggleCancelModal}
              confirm={this.cancelConfirmed}
              abort={this.toggleCancelModal}
          />
          <div className="apply-your-points-page">
            <MvLoader show={this.state.contractsRequestStatus === RequestStatus.LOADING}>
              <h1 className="page-header">
              {localize("static.content.ayp-reservation")}{': '}{localize("static.content.ayp-apply-your-points")}
              </h1>
              {
                <>
                  <BookingInfo
                      reservationDetails={this.state.reservationDetails}
                      pointsRequired={this.state.reservationDetails.pointsRequired}
                      onEditSearchClick={this.goBackToSelectAvailabilityElementOnSearchScreen}
                      readOnly={false}
                      setScreenType={this.props.setScreenType}
                  />
                  <MvSortTable
                    rowKey="usageId"
                    headers={this.eligiblePointsTableHeaders()}
                    rows={this.eligiblePointsTableRows()}
                    dataStatus={this.state.contractsRequestStatus}
                    headerSection={
                      <div className="eligible-points-header">
                        <h3>{localize("static.content.ayp-apply-your-points")}</h3>
                        <br />
                        <div className="ayp-label">
                          {localize("static.content.ayp-total-points-description")}
                        </div>
                        <br />
                        <div>
                          <span className="ayp-label">
                            {localize("static.content.ayp-total-points-required")}{": "}
                          </span>
                          {this.state.reservationDetails.pointsRequired}                
                        </div>
                      </div>
                    }
                    minWidth={1}
                    columnStyles={{}}
                    apiTimeout = {this.props.apiTimeout}
                  />
                  <section className="apply-your-points-summary">
                  {/* TODO: pass real data to PointsSummary */}
                    <PointsSummary
                      pointsRequired={pointsRequired}
                      pointsApplied={this.getPointsApplied()}
                      pointsStillNeeded={this.getPointsNeeded()}
                      onReset={this.resetSelections}
                      onContinue={this.continue}
                      onCancel={this.toggleCancelModal}
                       suggestedAutoApply={this.state.suggestedAutoApply}
                        showAutoAdjustError={this.state.showAutoAdjustError}
                        hasNoEligiblePoints={this.state.hasNoEligiblePoints}
                        autoAdjustUsage={this.state.autoAdjustUsage}
                    />
                  </section>
                    <div className="inelegible-table-container">
                      <MvSortTable
                        rowKey="usageId"
                        headers={this.ineligiblePointsTableHeaders()}
                        rows={this.ineligiblePointsTableRows()}
                        dataStatus={this.state.contractsRequestStatus}
                        headerSection={
                          <div className="ineligible-points-header">
                            <h3 className="">
                              {localize("static.content.ayp-ineligible-points")}
                            </h3>
                          </div>
                        }
                        minWidth={1}
                        columnStyles={{}}
                        tableExpandable={true}
                        tableExpanded={this.state.hasNoEligiblePoints}
                        apiTimeout = {this.props.apiTimeout}
                      />
                    </div>
                </>
              }
            </MvLoader>
          </div>
      </>
    );
  }
}

ApplyYourPoints.contextType = LocalizationContext;
export default ApplyYourPoints;
